<template>
  <PageTemplate heading="Nedlasting av fakturaer">
    <InfoCard>
      {{ translate('reports.invoiceDownload.infoCard.text') }}
    </InfoCard>

    <BlockContainer class="download-container mt-4">
      <div class="d-flex">
        <ReportSettingsLandlordSelector v-model="landlordId" class="dropdown" />
      </div>

      <!-- Periods -->
      <div v-if="landlordId === '__NULL__'">Du må velge en utleierprofil.</div>
      <div v-else-if="loadingPeriods">Henter perioder, vennligst vent...</div>
      <div v-else-if="loadingPeriodsError">
        <p>Kunne ikke hente rapport-perioder.</p>
        <p>
          Dette skyldes mest sannsynlig at det ikke er noen fakturaer på denne utleierprofilen. Hvis du mener dette er
          feil, ta kontakt med kundeservice.
        </p>
      </div>
      <div v-else>
        <div class="d-flex">
          <hl-Select
            label="Periode fra og med"
            v-model="periodStart"
            :options="periodOptions"
            placeholder="Velg periode"
            placeholderValue="__default__"
            class="dropdown mr-3"
          />
          <hl-Select
            label="Periode til og med"
            v-model="periodEnd"
            :options="periodOptions"
            placeholder="Velg periode"
            placeholderValue="__default__"
            class="dropdown"
          />
        </div>
        <hl-ErrorMessage v-if="showPeriodError">
          Periode fra og med må være samme som eller lavere enn Periode til og med
        </hl-ErrorMessage>
        <hl-Button
          v-if="periodsValid"
          variant="primary"
          :disabled="loadingDocuments"
          :loading="loadingDocuments"
          @click="loadDocuments"
        >
          {{ loadingDocuments ? 'Vennligst vent...' : 'Hent PDF-er' }}
        </hl-Button>
      </div>

      <div v-if="periodsValid">
        <hr />

        <!-- PDF link -->
        <div v-if="loadingDocuments">Henter fakturaer, vennligst vent...</div>
        <div v-else-if="loadingDocumentsError">
          <p>Kunne ikke hente fakturaer. Ta kontakt med kundeservice hvis du mener dette er feil.</p>
        </div>
        <div v-else-if="downloadUrl">
          <p>Fant 5 fakturaer for valgt periode</p>
          <a class="btn btn-primary" :href="downloadUrl">
            <i class="budicon budicon-files-download mr-3"></i>
            Last ned fakturaer
          </a>
        </div>
        <div v-else>
          Klikk "Hent PDF-er" for å laste fakturaer for valgt periode. Vær obs på at det kan ta et par minutter pga
          store datamengder.
        </div>
      </div>
    </BlockContainer>
  </PageTemplate>
</template>

<script>
import { LandlordReportApi } from '@modules/husleieapi2';
import BlockContainer from '@/components/BlockContainer';
import ReportSettingsLandlordSelector from '@/components/reports/ReportSettingsLandlordSelector';
import downloadBlob from '@/lib/downloadBlob';
import Button from '@modules/husleie-framework/src/components/buttons/Button.vue';
import Select from '@modules/husleie-framework/src/components/forms/inputs/select/Select.vue';
import ErrorMessage from '@modules/husleie-framework/src/components/text/ErrorMessage.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import InfoCard from '@/components/InfoCard.vue';
import i18nCommon from '@modules/i18n/mixins/Common';

const landlordReportApi = new LandlordReportApi();

export default {
  name: 'ReportInvoiceDownload',
  data() {
    return {
      loadingPeriods: false,
      loadingPeriodsError: false,
      periods: [],
      loadingDocuments: false,
      loadingDocumentsError: false,
      downloadUrl: null,
      landlordId: '__NULL__',
      periodStart: '__default__',
      periodEnd: '__default__',
    };
  },
  mixins: [i18nCommon],
  components: {
    BlockContainer,
    ReportSettingsLandlordSelector,
    PageTemplate,
    InfoCard,
    'hl-Button': Button,
    'hl-Select': Select,
    'hl-ErrorMessage': ErrorMessage,
  },
  computed: {
    periodOptions() {
      const options = [];
      if (!this.periods) {
        return options;
      }
      const fromState = this.periods.map(period => ({
        name: period,
        value: period,
      }));
      fromState.reverse();
      return options.concat(fromState);
    },
    periodsValid() {
      if (this.periodStart !== '__default__' && this.periodEnd !== '__default__') {
        const ps = new Date(this.periodStart + '-01');
        const pe = new Date(this.periodEnd + '-01');

        if (ps <= pe) return true;
      }

      return false;
    },
    showPeriodError() {
      return (this.periodStart !== '__default__' || this.periodEnd !== '__default__') && !this.periodsValid;
    },
  },
  watch: {
    landlordId: {
      async handler(newId) {
        console.log(newId);
        if (newId !== '__NULL__') {
          await this.loadPeriods(newId);
        }
      },
      immediate: true,
    },
    periodStart: {
      handler: 'onPeriodChange',
    },
    periodEnd: {
      handler: 'onPeriodChange',
    },
  },
  methods: {
    async loadPeriods(landlordId) {
      this.loadingPeriods = true;
      this.loadingPeriodsError = false;
      try {
        const res = await landlordReportApi.getLandlordAccountingReportsInvoices([landlordId], 'standard', {
          query: { format: 'json' },
        });
        this.periods = res.meta.periods;
      } catch (error) {
        this.loadingPeriodsError = true;
        console.error(error);
        this.$toast('Kunne ikke hente perioder.', 'error');
      } finally {
        this.loadingPeriods = false;
      }
    },
    async loadDocuments() {
      this.loadingDocuments = true;
      this.loadingDocumentsError = false;

      try {
        const blob = await landlordReportApi.getLandlordAccountingReportsInvoicesVouchersDownload(
          [this.landlordId],
          {
            query: {
              asOfPeriod: this.periodStart,
              evenPeriod: this.periodEnd,
            },
          },
          'blob'
        );

        downloadBlob(`fakturaer.zip`, blob);
      } catch (error) {
        this.loadingDocumentsError = true;
        console.error(error);

        this.$toast('Kunne ikke hente fakturaer.', 'error');
      } finally {
        this.loadingDocuments = false;
      }
    },
    onPeriodChange() {
      this.downloadUrl = null;
    },
  },
};
</script>

<style scoped lang="scss">
.download-container {
  padding: 1.25rem;
}

.dropdown {
  width: 100%;
  max-width: 20rem;
}
</style>
